// Firebase Cloud Messaging (web push) for the public website (CRA).
//
// enablePush requests permission + registers the FCM token. It is called
// automatically on login (see PushManager) and only triggers the native prompt
// while permission is still 'default' — a denied prompt is permanent. The service worker
// lives at `${PUBLIC_URL}/firebase-messaging-sw.js` and is registered with a matching
// scope, so it works whether the app is served from the domain root or a subpath
// (PUBLIC_URL is set from package.json "homepage" at build time).
import { initializeApp, getApps } from 'firebase/app';
import {
  getMessaging,
  getToken,
  deleteToken,
  onMessage,
  isSupported,
  type Messaging,
  type MessagePayload,
} from 'firebase/messaging';
import { firebaseConfig, firebaseVapidKey } from '../config/firebase';
import { apiPost, apiDelete } from '../api';
import { logger } from '../utils/logger';

const FCM_TOKEN_KEY = 'fcm_token';
const SW_URL = `${process.env.PUBLIC_URL || ''}/firebase-messaging-sw.js`;
const SW_SCOPE = `${process.env.PUBLIC_URL || ''}/`;

let messagingPromise: Promise<Messaging | null> | null = null;

async function getMessagingInstance(): Promise<Messaging | null> {
  if (!messagingPromise) {
    messagingPromise = (async () => {
      if (!(await isSupported().catch(() => false))) return null;
      if (!getApps().length) initializeApp(firebaseConfig);
      return getMessaging();
    })();
  }
  return messagingPromise;
}

export type PushPermission = NotificationPermission | 'unsupported';

export function pushPermission(): PushPermission {
  if (typeof Notification === 'undefined' || !('serviceWorker' in navigator)) return 'unsupported';
  return Notification.permission;
}

export function hasLocalToken(): boolean {
  return !!localStorage.getItem(FCM_TOKEN_KEY);
}

export interface EnableResult {
  ok: boolean;
  reason?: 'unsupported' | 'denied' | 'no-token' | 'error';
}

/** Request permission, obtain an FCM token, and register it with the backend. */
export async function enablePush(): Promise<EnableResult> {
  try {
    const messaging = await getMessagingInstance();
    if (!messaging) return { ok: false, reason: 'unsupported' };

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return { ok: false, reason: 'denied' };

    return registerCurrentToken(messaging);
  } catch (err) {
    logger.error('enablePush failed', err);
    return { ok: false, reason: 'error' };
  }
}

/**
 * Refresh/register the FCM token only when permission is already granted.
 * Does not show the native permission prompt — used on login bootstrap.
 */
export async function ensurePushToken(): Promise<EnableResult> {
  try {
    if (pushPermission() !== 'granted') {
      return { ok: false, reason: pushPermission() === 'unsupported' ? 'unsupported' : 'denied' };
    }
    const messaging = await getMessagingInstance();
    if (!messaging) return { ok: false, reason: 'unsupported' };
    return registerCurrentToken(messaging);
  } catch (err) {
    logger.error('ensurePushToken failed', err);
    return { ok: false, reason: 'error' };
  }
}

async function waitForActiveServiceWorker(
  registration: ServiceWorkerRegistration,
): Promise<ServiceWorkerRegistration | null> {
  if (registration.active) return registration;

  try {
    await navigator.serviceWorker.ready;
  } catch (err) {
    logger.warn('serviceWorker.ready failed', err);
  }

  if (registration.active) return registration;

  const worker = registration.installing || registration.waiting;
  if (worker) {
    await new Promise<void>((resolve) => {
      const done = () => resolve();
      const timer = window.setTimeout(done, 8000);
      worker.addEventListener('statechange', () => {
        if (worker.state === 'activated' || worker.state === 'redundant') {
          window.clearTimeout(timer);
          done();
        }
      });
      if (worker.state === 'activated') {
        window.clearTimeout(timer);
        done();
      }
    });
  }

  return registration.active ? registration : null;
}

async function registerCurrentToken(messaging: Messaging): Promise<EnableResult> {
  try {
    if (!('serviceWorker' in navigator)) {
      return { ok: false, reason: 'unsupported' };
    }

    let registration = await navigator.serviceWorker.getRegistration(SW_SCOPE);
    if (!registration) {
      registration = await navigator.serviceWorker.register(SW_URL, { scope: SW_SCOPE });
    }

    const activeRegistration = await waitForActiveServiceWorker(registration);
    if (!activeRegistration?.active) {
      logger.warn('FCM skipped: no active service worker', { swUrl: SW_URL, scope: SW_SCOPE });
      return { ok: false, reason: 'error' };
    }

    const token = await getToken(messaging, {
      vapidKey: firebaseVapidKey,
      serviceWorkerRegistration: activeRegistration,
    });
    if (!token) return { ok: false, reason: 'no-token' };

    await apiPost('/notifications/device-token', { token, platform: 'web' });
    localStorage.setItem(FCM_TOKEN_KEY, token);
    return { ok: true };
  } catch (err) {
    logger.error('registerCurrentToken failed', err);
    return { ok: false, reason: 'error' };
  }
}

export async function disablePush(): Promise<void> {
  const token = localStorage.getItem(FCM_TOKEN_KEY);
  try {
    const messaging = await getMessagingInstance();
    if (messaging) await deleteToken(messaging);
  } catch (err) {
    logger.error('deleteToken failed', err);
  }
  if (token) {
    try { await apiDelete('/notifications/device-token', { body: JSON.stringify({ token }) }); } catch { /* ignore */ }
  }
  localStorage.removeItem(FCM_TOKEN_KEY);
}

export async function onForegroundMessage(
  cb: (payload: MessagePayload) => void,
): Promise<() => void> {
  const messaging = await getMessagingInstance();
  if (!messaging) return () => {};
  return onMessage(messaging, cb);
}
